import React, { useState } from 'react';
import { connect } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { doClaimCase } from '../../actions/iciciQueue';
import { setActiveConsult } from '../../actions';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-12px) scale(0.97); }
  to   { opacity: 1; transform: none; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9100;
`;

const Card = styled.div`
  background: #1a1d26;
  border: 1px solid #2a2d3a;
  border-radius: 1.2rem;
  width: 44rem;
  max-width: 95vw;
  padding: 2.4rem;
  animation: ${fadeIn} 0.3s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #f1f5f9;
  position: relative;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.6rem;
`;

const CardLabel = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #8852cc;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const DismissBtn = styled.button`
  background: transparent;
  border: 1px solid #2a2d3a;
  color: #94a3b8;
  border-radius: 0.4rem;
  width: 2.8rem;
  height: 2.8rem;
  font-size: 1.4rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { border-color: #ef4444; color: #ef4444; }
`;

const CustomerName = styled.div`
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 0.6rem;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.8rem 1.4rem;
  margin-top: 1.2rem;
`;

const MetaLabel = styled.div`font-size: 1.1rem; color: #94a3b8;`;
const MetaValue = styled.div`font-size: 1.3rem; font-weight: 600;`;

const WaitBadge = styled.div`
  display: inline-flex;
  align-items: center;
  font-size: 1.2rem;
  font-weight: 600;
  color: #f59e0b;
  background: rgba(245,158,11,0.13);
  padding: 0.25rem 0.8rem;
  border-radius: 0.4rem;
  margin-top: 0.6rem;
`;

const StartBtn = styled.button`
  width: 100%;
  padding: 1.4rem;
  margin-top: 2rem;
  background: #22c55e;
  color: #fff;
  border: none;
  border-radius: 0.8rem;
  font-size: 1.6rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.88; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const StatusMsg = styled.div`
  margin-top: 1rem;
  font-size: 1.2rem;
  text-align: center;
  color: ${(p) => p.error ? '#ef4444' : '#94a3b8'};
`;

const fmtSeconds = (s) => {
  if (!s && s !== 0) return null;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

function IciciCaseNotification({ caseData, onDismiss, dispatch }) {
  const [claiming, setClaiming] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
  const [isError, setIsError] = useState(false);

  const handleStartCall = async () => {
    if (claiming) return;
    setClaiming(true);
    setStatusMsg(null);

    const result = await dispatch(doClaimCase(caseData.consultationId));

    if (!result) {
      setStatusMsg('Something went wrong. Please try again.');
      setIsError(true);
      setClaiming(false);
      return;
    }

    if (result.status === 'already_taken') {
      setStatusMsg('This case was already taken by another doctor.');
      setIsError(true);
      setClaiming(false);
      return;
    }

    if (result.status === 'success') {
      dispatch(setActiveConsult({
        consultationId: caseData.consultationId,
        callId: result.callId,
        user: {
          userId: caseData.userId || result.userId || null,
          userName: caseData.customerName,
          gender: caseData.gender || '',
        },
        labels: [],
        oneConnectLinks: [],
        relatives: [],
        requestType: 'video',
        callType: 'video',
        channel: `icici-instant-${caseData.consultationId}`,
      }));
      dispatch({
        type: 'INITIATE_VIDEO_CALL',
        payload: {
          isInsta: 1,
          instantConsultationId: caseData.consultationId,
        },
      });
      onDismiss();
    }
  };

  const waitStr = fmtSeconds(caseData.waitSeconds);

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onDismiss(); }}>
      <Card>
        <CardHeader>
          <CardLabel>Incoming Case</CardLabel>
          <DismissBtn onClick={onDismiss} title="Dismiss">✕</DismissBtn>
        </CardHeader>

        <CustomerName>{caseData.customerName}</CustomerName>
        {waitStr && <WaitBadge>Waited {waitStr}</WaitBadge>}

        <MetaGrid>
          <div>
            <MetaLabel>Application ID</MetaLabel>
            <MetaValue>{caseData.applicationId || '—'}</MetaValue>
          </div>
          <div>
            <MetaLabel>Gender</MetaLabel>
            <MetaValue>{caseData.gender || '—'}</MetaValue>
          </div>
          <div>
            <MetaLabel>Phone</MetaLabel>
            <MetaValue>{caseData.phone || '—'}</MetaValue>
          </div>
        </MetaGrid>

        <StartBtn onClick={handleStartCall} disabled={claiming}>
          {claiming ? 'Connecting…' : 'Start Call Now'}
        </StartBtn>

        {statusMsg && <StatusMsg error={isError}>{statusMsg}</StatusMsg>}
      </Card>
    </Overlay>
  );
}

const mapStateToProps = () => ({});

export default connect(mapStateToProps)(IciciCaseNotification);
